import {
  Dispatch,
  MouseEvent,
  MouseEventHandler,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import {
  Button,
  ButtonGroup,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grow,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from "@mui/material"
import {
  ArrowDropDownRounded,
  ArrowDropUpRounded,
  ChevronLeftRounded,
  UndoRounded,
  TranslateRounded,
} from "@mui/icons-material"
import { MainContext } from "../../controllers/main"
import { logger, Status } from "../../services/utilities/utility"
import CopyToClipboardButton from "./copyToClipboardButton"
import { CSVLink } from "react-csv"
import LoadingBackdrop from "./loadingBackdrop"
import { LoadingButton } from "@mui/lab"
import NotionIcon from "../../assets/icons/NotionIcon"

const EditHeading = ({
  showBackButton = true,
  backButtonOnClick,
  title,
  titleLoading,
  id,
  saveButtonDisabled,
  undoButtonDisabled,
  saveButtonOnClick,
  undoButtonOnClick,
  saveButtonLoading = false,
  showButtons = true,
  showEditButton = false,
  showArchiveButton = false,
  archiveButtonOnClick,
  showPreviewButton = false,
  previewButtonDisabled,
  showPreviewButtonOnClick,
  editModeActive = false,
  editSwitchDisabled = false,
  setEditModeActive,
  doneChanges,
  showInfoButton = false,
  infoButtonOnClick,
  showDeleteButton = false,
  deleteButtonOnClick,
  showDuplicateButton = false,
  duplicateButtonOnClick,
  showAutojoinButton = false,
  autojoinButtonOnClick,
  showCsvButton = false,
  csvButtonOnClick,
  csvFileName,
  csvButtonType = "default",
  showAddButton = false,
  addButtonLabel,
  addButtonOnClick,
  showBadge = false,
  badgeLabel,
  showSyncButton = false,
  syncButtonOnClick,
  syncButtonLoading,
  showAiTranslateButton = false,
  aiTranslateButtonOnClick,
  showCustomButton,
  customButtonOnClick,
  customButtonLabel,
}: {
  showBackButton?: boolean
  backButtonOnClick?: MouseEventHandler<HTMLButtonElement>
  title: string
  titleLoading?: boolean
  id?: string
  saveButtonDisabled?: boolean
  undoButtonDisabled?: boolean
  saveButtonOnClick?: MouseEventHandler<HTMLButtonElement>
  undoButtonOnClick?: MouseEventHandler<HTMLButtonElement>
  saveButtonLoading?: boolean
  showButtons?: boolean
  showEditButton?: boolean
  showArchiveButton?: boolean
  archiveButtonOnClick?: MouseEventHandler<HTMLButtonElement>
  showPreviewButton?: boolean
  previewButtonDisabled?: boolean
  showPreviewButtonOnClick?: () => void
  editModeActive?: boolean
  editSwitchDisabled?: boolean
  setEditModeActive?: Dispatch<SetStateAction<boolean>>
  doneChanges?: boolean
  showInfoButton?: boolean
  infoButtonOnClick?: MouseEventHandler<HTMLButtonElement>
  showDeleteButton?: boolean
  deleteButtonOnClick?: MouseEventHandler<HTMLButtonElement>
  showDuplicateButton?: boolean
  duplicateButtonOnClick?: MouseEventHandler<HTMLButtonElement>
  showAutojoinButton?: boolean
  autojoinButtonOnClick?: () => void
  showCsvButton?: boolean
  csvButtonOnClick?: (withEmails?: boolean) => Promise<string[][]>
  csvFileName?: string
  csvButtonType?: "default" | "dropdown"
  showAddButton?: boolean
  addButtonLabel?: string
  addButtonOnClick?: () => void
  showBadge?: boolean
  badgeLabel?: string
  showSyncButton?: boolean
  syncButtonOnClick?: () => void
  syncButtonLoading?: boolean
  showAiTranslateButton?: boolean
  aiTranslateButtonOnClick?: () => void
  showCustomButton?: boolean
  customButtonOnClick?: () => void
  customButtonLabel?: string
}) => {
  const { animation } = useContext(MainContext)

  // undo dialog
  const [undoDialogOpen, setUndoDialogOpen] = useState<boolean>(false)

  // csv download button
  const csvButtonRef = useRef(null)
  const [csvData, setCsvData] = useState<string[][]>([])
  const [csvLoading, setCsvLoading] = useState<boolean>(false)

  useEffect(() => {
    if (csvData.length && !csvLoading) {
      csvButtonRef.current.link.click()
    }
  }, [csvData])

  // csv button dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  return (
    <Grow
      in={animation}
      timeout={250}
      style={{ marginTop: 16, paddingLeft: 16, paddingRight: 16, flex: 1 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={2}
        sx={{ maxWidth: "100%", maxHeight: 38 }}
      >
        {showBackButton && (
          <Button variant="outlined" onClick={backButtonOnClick}>
            <ChevronLeftRounded />
          </Button>
        )}
        <Stack
          style={{
            width: "-webkit-fill-available",
            overflow: "hidden",
          }}
          spacing={-0.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            {titleLoading ? (
              <Skeleton width={220} style={{ fontSize: 18 }} />
            ) : (
              <Typography
                variant="h6"
                style={{
                  width: showBadge ? "auto" : "100%",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  lineHeight: 1.5,
                }}
              >
                {title}
              </Typography>
            )}
            {showBadge && (
              <Chip
                size="small"
                color="info"
                label={badgeLabel}
                style={{
                  backgroundColor: "#5562B4",
                  height: 18,
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />
            )}
          </Stack>
          {id ? (
            <Stack
              direction="row"
              alignItems="center"
              style={{ marginLeft: -3 }}
            >
              <CopyToClipboardButton textToCopy={id} />
              <Typography
                variant="caption"
                style={{
                  width: "100%",
                  fontSize: 10,
                  color: "gray",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {id}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
        {showCsvButton && (
          <div
            style={{
              width: 166,
              minWidth: 166,
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "row",
            }}
          >
            {csvButtonType === "default" ? (
              <Button
                variant="contained"
                onClick={async () => {
                  setCsvLoading(true)
                  setCsvData([])
                  const data = await csvButtonOnClick()
                  setCsvData(data)
                  setCsvLoading(false)
                }}
              >
                Export
              </Button>
            ) : (
              <div>
                <Button
                  id="basic-button"
                  aria-controls={open ? "basic-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  onClick={handleClick}
                  variant="contained"
                  endIcon={
                    open ? <ArrowDropUpRounded /> : <ArrowDropDownRounded />
                  }
                >
                  Export
                </Button>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                  <MenuItem
                    onClick={async () => {
                      handleClose()
                      setCsvLoading(true)
                      setCsvData([])
                      const data = await csvButtonOnClick(false)
                      setCsvData(data)
                      setCsvLoading(false)
                    }}
                  >
                    Without emails
                  </MenuItem>
                  <MenuItem
                    onClick={async () => {
                      handleClose()
                      setCsvLoading(true)
                      setCsvData([])
                      const data = await csvButtonOnClick(true)
                      setCsvData(data)
                      setCsvLoading(false)
                    }}
                  >
                    With emails
                  </MenuItem>
                </Menu>
              </div>
            )}
            <CSVLink
              data={csvData}
              ref={csvButtonRef}
              filename={csvFileName}
              style={{
                textDecoration: "none",
              }}
            />
          </div>
        )}
        {showAddButton && addButtonOnClick ? (
          <Button
            variant="contained"
            onClick={addButtonOnClick}
            style={{ width: 95, minWidth: 95 }}
          >
            {addButtonLabel}
          </Button>
        ) : null}
        {showEditButton && (
          <div
            style={{
              width: "-webkit-fill-available",
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "row",
              gap: 16,
            }}
          >
            {showAiTranslateButton && (
              <Button
                variant="contained"
                onClick={aiTranslateButtonOnClick}
                disabled={!editModeActive}
                startIcon={<TranslateRounded />}
              >
                AI
              </Button>
            )}
            {showDeleteButton && (
              <Button
                variant="contained"
                color="error"
                onClick={deleteButtonOnClick}
                disabled={!editModeActive}
              >
                Delete
              </Button>
            )}
            {showAutojoinButton && (
              <Button
                variant="contained"
                disabled={!editModeActive}
                onClick={autojoinButtonOnClick}
              >
                Autojoin
              </Button>
            )}
            {showInfoButton && (
              <Button variant="contained" onClick={infoButtonOnClick}>
                Info
              </Button>
            )}
            {showPreviewButton ? (
              <Button
                variant="contained"
                onClick={showPreviewButtonOnClick}
                disabled={previewButtonDisabled}
              >
                Preview
              </Button>
            ) : null}
            {showSyncButton && (
              <LoadingButton
                variant="contained"
                loading={syncButtonLoading}
                startIcon={<NotionIcon />}
                onClick={syncButtonOnClick}
                disabled={!editModeActive}
              >
                Sync
              </LoadingButton>
            )}
            {showDuplicateButton && (
              <Button
                variant="contained"
                onClick={duplicateButtonOnClick}
                disabled={!editModeActive}
              >
                Duplicate
              </Button>
            )}
            {showArchiveButton && (
              <Button
                variant="contained"
                onClick={archiveButtonOnClick}
                disabled={!editModeActive}
              >
                Archive
              </Button>
            )}
            {showButtons && (
              <ButtonGroup
                variant="outlined"
                aria-label="outlined button group"
              >
                <Button
                  onClick={() => {
                    setUndoDialogOpen(true)
                  }}
                  disabled={
                    undoButtonDisabled ||
                    saveButtonLoading ||
                    !editModeActive ||
                    !doneChanges
                  }
                >
                  <UndoRounded />
                </Button>
                <Button
                  variant="contained"
                  disabled={
                    saveButtonDisabled ||
                    saveButtonLoading ||
                    !editModeActive ||
                    !doneChanges
                  }
                  onClick={
                    saveButtonOnClick
                      ? saveButtonOnClick
                      : () => {
                          logger(Status.Warning, "No actions")
                        }
                  }
                  style={{ whiteSpace: "nowrap" }}
                >
                  Save changes
                </Button>
              </ButtonGroup>
            )}
            <FormControlLabel
              labelPlacement="start"
              control={<Switch />}
              label="Edit"
              style={{ marginLeft: 0, minWidth: 86 }}
              checked={editModeActive}
              disabled={editSwitchDisabled}
              onChange={() => {
                setEditModeActive(!editModeActive)
              }}
            />
          </div>
        )}
        {showCustomButton && (
          <Button
            variant="contained"
            onClick={customButtonOnClick}
            style={{ whiteSpace: "nowrap" }}
          >
            {customButtonLabel}
          </Button>
        )}
        {showButtons && (
          <Dialog
            open={undoDialogOpen}
            onClose={() => {
              setUndoDialogOpen(false)
            }}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              Are you sure you want to undo all changes?
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                All changes will be lost
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setUndoDialogOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={(e) => {
                  undoButtonOnClick(e)
                  setUndoDialogOpen(false)
                }}
                autoFocus
              >
                Undo
              </Button>
            </DialogActions>
          </Dialog>
        )}
        <LoadingBackdrop open={csvLoading} style={{ left: -16 }} />
      </Stack>
    </Grow>
  )
}

export default EditHeading
